import { DOCUMENT_TYPE } from "@/pages-components/quotes/enums";

export type PackagingDocumentKind =
  | "quote"
  | "order"
  | "invoice"
  | "deliveryNote";

export type CartDisplayMode = "combined" | "separate" | "hidden";

export type PackagingPriceDisplayMode = "defaultZero" | "actual";

export type PackagingInitialSection = "packaging" | "shipping";

/**
 * Which cart line asked to open Pack & Ship ("Edit" in the cart menu).
 * "delivery" is resolved against the saved cart display mode, because a
 * combined line is a delivery line that also carries the packaging price.
 */
export type PackagingSectionRequest = "packaging" | "delivery";

/** Footer "Delete" menu actions of the Pack & Ship modal. */
export type PackagingDeleteAction = "delivery" | "packaging" | "both";

/**
 * Cart display mode that still matches what is left after a footer Delete.
 * "combined" merges packaging and shipping into one line, so it falls back to
 * "separate" once either part is removed; "hidden" (price spread across cart
 * items) only falls back when nothing is left to spread.
 */
export const resolveCartDisplayModeAfterDelete = (
  mode: CartDisplayMode,
  action: PackagingDeleteAction,
): CartDisplayMode => {
  if (mode === "combined") {
    return "separate";
  }
  if (mode === "hidden" && action === "both") {
    return "separate";
  }
  return mode;
};

/**
 * Maps a cart "Edit" request to the section to expand, or null when the modal
 * was opened from another entry point (the document default applies).
 * A delivery line expands Shipping, except for a combined Pack & Ship line
 * (saved mode "combined"), which expands Packaging.
 */
export const resolveRequestedPackagingSection = (
  request: PackagingSectionRequest | null | undefined,
  {
    cartDisplayMode,
    isCartDisplayModeResolved,
  }: {
    cartDisplayMode: CartDisplayMode;
    isCartDisplayModeResolved: boolean;
  },
): PackagingInitialSection | null => {
  if (request === "packaging") {
    return "packaging";
  }
  if (request === "delivery") {
    return isCartDisplayModeResolved && cartDisplayMode === "combined"
      ? "packaging"
      : "shipping";
  }
  return null;
};

/** Parses a `DEFAULT_PACK_AND_SHIP_SECTION` option (PACKAGING / SHIPPING). */
export const parsePackagingInitialSection = (
  value: unknown,
): PackagingInitialSection | null => {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  return normalized === "packaging" || normalized === "shipping"
    ? normalized
    : null;
};

/**
 * Section Pack & Ship opens on when no cart "Edit" shortcut asked for one.
 * Quote and Order follow the `DEFAULT_PACK_AND_SHIP_SECTION` preference
 * (Packaging when missing or invalid). Invoice and Delivery Note always open
 * Shipping and ignore the preference.
 */
export const resolveDefaultPackagingSection = (
  documentKind: PackagingDocumentKind,
  ...preferenceCandidates: unknown[]
): PackagingInitialSection => {
  if (documentKind === "invoice" || documentKind === "deliveryNote") {
    return PACKAGING_DOCUMENT_KINDS[documentKind].initialSection;
  }
  for (const candidate of preferenceCandidates) {
    const section = parsePackagingInitialSection(candidate);
    if (section) {
      return section;
    }
  }
  return PACKAGING_DOCUMENT_KINDS[documentKind].initialSection;
};

export const CART_DISPLAY_MODES: CartDisplayMode[] = [
  "combined",
  "separate",
  "hidden",
];

export interface PackagingDocumentKindConfig {
  kind: PackagingDocumentKind;
  documentIdKey: "quoteId" | "orderId" | "invoiceId" | "deliveryNoteId";
  packagesPath: string;
  hierarchyPath: string;
  optimizeSegment: string;
  /** Numeric document type used by get-document and packaging-item save. */
  documentType: DOCUMENT_TYPE;
  initialSection: PackagingInitialSection;
}

export const PACKAGING_DOCUMENT_KINDS: Record<
  PackagingDocumentKind,
  PackagingDocumentKindConfig
> = {
  quote: {
    kind: "quote",
    documentIdKey: "quoteId",
    packagesPath: "/v1/packaging-service/package/quote",
    hierarchyPath: "/v1/packaging-service/package/quote-hierarchies",
    optimizeSegment: "quote",
    documentType: DOCUMENT_TYPE.quote,
    initialSection: "packaging",
  },
  order: {
    kind: "order",
    documentIdKey: "orderId",
    packagesPath: "/v1/packaging-service/package/order",
    hierarchyPath: "/v1/packaging-service/package/order-hierarchies",
    optimizeSegment: "order",
    documentType: DOCUMENT_TYPE.order,
    initialSection: "packaging",
  },
  invoice: {
    kind: "invoice",
    documentIdKey: "invoiceId",
    packagesPath: "/v1/packaging-service/package/invoice",
    hierarchyPath: "/v1/packaging-service/package/invoice-hierarchies",
    optimizeSegment: "invoice",
    documentType: DOCUMENT_TYPE.invoice,
    initialSection: "shipping",
  },
  deliveryNote: {
    kind: "deliveryNote",
    documentIdKey: "deliveryNoteId",
    packagesPath: "/v1/packaging-service/package/delivery-note",
    hierarchyPath: "/v1/packaging-service/package/delivery-note-hierarchies",
    optimizeSegment: "delivery-note",
    documentType: DOCUMENT_TYPE.deliveryNote,
    initialSection: "shipping",
  },
};

export const isPackagingDocumentKind = (
  value: unknown,
): value is PackagingDocumentKind =>
  value === "quote" ||
  value === "order" ||
  value === "invoice" ||
  value === "deliveryNote";

export const parseCartDisplayMode = (
  value: unknown,
): CartDisplayMode | null => {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  return (CART_DISPLAY_MODES as string[]).includes(normalized)
    ? (normalized as CartDisplayMode)
    : null;
};

/** Used only when the print-house preference is missing or invalid. */
export const DEFAULT_CART_DISPLAY_MODE: CartDisplayMode = "separate";

/**
 * A mode the user saved wins over the print-house preference.
 * A missing mode is normalized to "separate" by the APIs, so "nothing saved"
 * is detected from hierarchy id and shippingType, not from the string itself.
 *
 * The package GET also creates a default hierarchy row (nodes flagged
 * isDefault, mode "separate") the first time the modal opens. That row is not
 * a user choice, so `hierarchyIsDefault` makes it fall through to the preference.
 */
export const resolveInitialCartDisplayMode = ({
  hierarchyId,
  hierarchyIsDefault = false,
  packagingCartDisplayMode,
  shippingSaved,
  shippingCartDisplayMode,
  preference,
}: {
  hierarchyId: string | null | undefined;
  hierarchyIsDefault?: boolean;
  packagingCartDisplayMode: CartDisplayMode | null | undefined;
  shippingSaved: boolean;
  shippingCartDisplayMode: CartDisplayMode | null | undefined;
  preference: unknown;
}): CartDisplayMode => {
  if (hierarchyId && !hierarchyIsDefault) {
    return packagingCartDisplayMode ?? DEFAULT_CART_DISPLAY_MODE;
  }
  if (shippingSaved) {
    return shippingCartDisplayMode ?? DEFAULT_CART_DISPLAY_MODE;
  }
  return parseCartDisplayMode(preference) ?? DEFAULT_CART_DISPLAY_MODE;
};

/**
 * Lights the Pack & Ship button from the document flag isPackAndShipConfigured.
 */
export const documentShowsPackAndShipConfigured = (
  document:
    | {
        isPackAndShipConfigured?: boolean;
        IsPackAndShipConfigured?: boolean;
      }
    | null
    | undefined,
): boolean => {
  if (!document) {
    return false;
  }
  return (
    document.isPackAndShipConfigured === true ||
    document.IsPackAndShipConfigured === true
  );
};
